import React, { useEffect, useState } from 'react';
import type { CmsPage, SiteLayoutData } from '../types';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { RenderCmsBlockPreview, PageEditor } from '../components/Editor/PageEditor';
import {
  Map,
  Shield,
  BookOpen,
  Skull,
  Globe,
  Sparkles,
  ArrowRight,
  Edit3,
} from 'lucide-react';

interface HomePageProps {
  isAdmin: boolean;
  allPages: CmsPage[];
}

export const HomePage: React.FC<HomePageProps> = ({ isAdmin, allPages }) => {
  const [pageData, setPageData] = useState<CmsPage | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [holidayGreeting, setHolidayGreeting] = useState<string>('');

  useEffect(() => {
    Promise.all([
      fetch('/api/pages/index').then((r) => (r.ok ? (r.json() as Promise<CmsPage>) : null)),
      fetch('/api/layout').then((r) => (r.ok ? (r.json() as Promise<SiteLayoutData>) : null)),
    ])
      .then(([page, layout]) => {
        if (page) {
          setPageData(page);
        } else {
          setPageData({
            slug: 'index',
            pageName: 'Home',
            body: [],
            metaDescription: 'Welcome to the official Echocondria tabletop RPG compendium and world atlas.',
          });
        }
        if (layout && layout.holidayGreeting) {
          setHolidayGreeting(layout.holidayGreeting);
        }
      })
      .catch((e) => console.error('Failed loading home data:', e))
      .finally(() => setLoading(false));
  }, []);

  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const featureCards = [
    {
      title: 'City & World Atlas',
      desc: 'Interactive pan/zoom maps of Echocondria with categorized landmark hotspots and neighborhood districts.',
      url: '/maps',
      icon: <Map className="text-primary" size={28} />,
      badge: 'Atlas',
    },
    {
      title: 'Items & Equipment',
      desc: '700+ weapons, armors, consumables, instruments, and crafting gear with tag filtering and stat breakdowns.',
      url: '/items',
      icon: <BookOpen className="text-primary" size={28} />,
      badge: '700+ Items',
    },
    {
      title: 'Enemies & Bestiary',
      desc: 'Over 140 creatures, bandits, machines, and monstrosities complete with combat stats, attacks, and loot drops.',
      url: '/enemies',
      icon: <Skull className="text-primary" size={28} />,
      badge: '140+ Enemies',
    },
    {
      title: 'Classes & Races',
      desc: 'Explore character origins, species stat blocks, and unique progression paths tailored for the setting.',
      url: '/classes',
      icon: <Shield className="text-primary" size={28} />,
      badge: 'Character Rules',
    },
    {
      title: 'Abilities & Skills',
      desc: 'Hundreds of active skills, magical techniques, passive perks, and talent trees across all disciplines.',
      url: '/abilities',
      icon: <Sparkles className="text-primary" size={28} />,
      badge: '280+ Abilities',
    },
    {
      title: 'Quests & Lore',
      desc: 'Factions, city venues, side quests, NPCs, and comprehensive lexicon entries detailing the living world.',
      url: '/quests',
      icon: <Globe className="text-primary" size={28} />,
      badge: 'World Compendium',
    },
  ];

  return (
    <div className="home-page-container">
      {/* Hero Section */}
      <section className="page-hero py-5">
        <div className="container py-lg-4">
          <div className="row align-items-center gy-4">
            <div className="col-lg-8">
              <Breadcrumbs items={[{ label: 'Home', url: '/' }]} />
              <h1 className="hero-title display-4 fw-bold mb-3">Echocondria</h1>
              <p className="lead text-secondary mb-3">
                An expansive tabletop roleplaying setting, comprehensive game database, world atlas, and rulebook.
              </p>
              {holidayGreeting && (
                <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill badge bg-secondary-subtle small mb-3">
                  <span>{holidayGreeting}</span>
                </div>
              )}
            </div>
            <div className="col-lg-4 text-lg-end">
              {isAdmin && !isEditing && (
                <button
                  type="button"
                  className="btn btn-warning d-inline-flex align-items-center gap-2"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit3 size={16} /> Edit Home Page
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="container py-5">
        {/* Admin Editor on Home Page */}
        {isAdmin && isEditing && pageData && (
          <PageEditor
            initialPage={pageData}
            allPages={allPages}
            onSaveSuccess={(updated) => {
              setPageData(updated);
              setIsEditing(false);
            }}
            onExit={() => setIsEditing(false)}
          />
        )}

        {/* Dynamic CMS Page Canvas */}
        {pageData && pageData.body && pageData.body.length > 0 && (
          <div className="cms-page-canvas mb-5">
            {pageData.body.map((block, idx) => (
              <RenderCmsBlockPreview key={block.id || idx} block={block} />
            ))}
          </div>
        )}

        {/* Quick Explore Section */}
        <div className="my-4">
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <div>
              <p className="eyebrow mb-1">EXPLORE</p>
              <h2 className="fw-bold mb-0">Game Systems & Compendium</h2>
            </div>
          </div>

          <div className="row g-4">
            {featureCards.map((card, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border rounded-4 shadow-sm hover-lift p-3 d-flex flex-column justify-content-between">
                  <div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="p-2 rounded-3 border bg-secondary-subtle">{card.icon}</div>
                      <span className="badge bg-secondary-subtle small">{card.badge}</span>
                    </div>
                    <h5 className="card-title fw-bold mb-2">{card.title}</h5>
                    <p className="card-text small mb-4">{card.desc}</p>
                  </div>
                  <a
                    href={card.url}
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between mt-auto"
                    onClick={(e) => navigateTo(e, card.url)}
                  >
                    <span>Explore {card.title}</span>
                    <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
